/** Browser/server shared coordinate and admission contract. No renderer or I/O. */
export const REGION_CONTRACT = 'valoria.region/v1';
export class RegionError extends Error {
  constructor(message, code = 'INVALID_REGION') { super(message); this.name = 'RegionError'; this.code = code; }
}
const fail = (message, code) => { throw new RegionError(message, code); };
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
function keys(value, allowed, label) {
  if (!object(value)) fail(`${label} must be an object`);
  for (const key of Object.keys(value)) if (!allowed.includes(key)) fail(`Unknown ${label} field: ${key}`);
}
function number(value, min, max, label, integer = false) {
  if (!Number.isFinite(value) || value < min || value > max || (integer && !Number.isSafeInteger(value))) fail(`Invalid ${label}`);
  return value;
}
function id(value) { if (typeof value !== 'string' || !/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/.test(value)) fail('Invalid identifier'); return value; }
const revision = value => number(value, 0, Number.MAX_SAFE_INTEGER, 'revision', true);
function point(value) {
  keys(value, ['x', 'y', 'z'], 'point');
  return Object.fromEntries(['x', 'y', 'z'].map(axis => [axis, number(value[axis], -1e7, 1e7, axis)]));
}
function freeze(value) { if (object(value) || Array.isArray(value)) { Object.values(value).forEach(freeze); Object.freeze(value); } return value; }
export function validateRegion(input) {
  keys(input, ['contract', 'id', 'revision', 'columns', 'rows', 'tileSize', 'anchorDistrictId', 'districts'], 'region');
  if (input.contract !== REGION_CONTRACT) fail('Unsupported region contract');
  const columns = number(input.columns, 1, 63, 'columns', true), rows = number(input.rows, 1, 63, 'rows', true);
  if (!(columns % 2) || !(rows % 2)) fail('Centered regions require odd dimensions');
  keys(input.tileSize, ['x', 'z'], 'tileSize');
  const tileSize = {x: number(input.tileSize.x, 1, 10000, 'tile width'), z: number(input.tileSize.z, 1, 10000, 'tile depth')};
  if (!Array.isArray(input.districts) || input.districts.length > columns * rows) fail('Invalid district assignments');
  const ids = new Set(), cells = new Set();
  const districts = input.districts.map(value => {
    keys(value, ['id', 'worldId', 'revision', 'column', 'row'], 'district');
    const district = {id: id(value.id), worldId: id(value.worldId), revision: revision(value.revision),
      column: number(value.column, 0, columns - 1, 'column', true), row: number(value.row, 0, rows - 1, 'row', true)};
    const cell = `${district.column}:${district.row}`;
    if (ids.has(district.id) || cells.has(cell)) fail('District IDs and assigned cells must be unique');
    ids.add(district.id); cells.add(cell); return district;
  });
  const anchorDistrictId = id(input.anchorDistrictId), anchor = districts.find(d => d.id === anchorDistrictId);
  if (!anchor || anchor.column !== (columns - 1) / 2 || anchor.row !== (rows - 1) / 2) fail('Anchor must occupy the center cell');
  return freeze({contract: REGION_CONTRACT, id: id(input.id), revision: revision(input.revision), columns, rows, tileSize, anchorDistrictId, districts});
}
/** References existing content, never duplicates or generates city geometry. */
export function createDefaultRegion({cityWorldId = 'valoria', cityRevision = 0} = {}) {
  return validateRegion({contract: REGION_CONTRACT, id: 'valoria-region', revision: 0, columns: 7, rows: 7,
    tileSize: {x: 160, z: 160}, anchorDistrictId: 'city-center',
    districts: [{id: 'city-center', worldId: cityWorldId, revision: cityRevision, column: 3, row: 3}]});
}
export function getDistrict(region, districtId) {
  const district = region.districts.find(d => d.id === districtId);
  if (!district) fail('District is not assigned', 'DISTRICT_UNAVAILABLE');
  return district;
}
export function cellOrigin(region, column, row) {
  number(column, 0, region.columns - 1, 'column', true); number(row, 0, region.rows - 1, 'row', true);
  return {x: (column - (region.columns - 1) / 2) * region.tileSize.x, y: 0, z: (row - (region.rows - 1) / 2) * region.tileSize.z};
}
export function districtOrigin(region, districtId) { const d = getDistrict(region, districtId); return cellOrigin(region, d.column, d.row); }
export function toRegionPoint(region, districtId, local) {
  const p = point(local), origin = districtOrigin(region, districtId);
  return {x: p.x + origin.x, y: p.y, z: p.z + origin.z};
}
export function toDistrictPoint(region, districtId, global) {
  const p = point(global), origin = districtOrigin(region, districtId);
  return {x: p.x - origin.x, y: p.y, z: p.z - origin.z};
}
/** Half-open cells prevent ambiguous seam ownership; the outer max edge is outside. */
export function locateCell(region, global) {
  const p = point(global);
  const column = Math.floor(p.x / region.tileSize.x + region.columns / 2);
  const row = Math.floor(p.z / region.tileSize.z + region.rows / 2);
  if (column < 0 || row < 0 || column >= region.columns || row >= region.rows) return null;
  return {column, row, districtId: region.districts.find(d => d.column === column && d.row === row)?.id ?? null};
}
/** Same translation used for the render root must be used for collision boxes. */
export function toRegionCollider(region, districtId, box) {
  keys(box, ['minX', 'maxX', 'minY', 'maxY', 'minZ', 'maxZ'], 'collider');
  for (const axis of ['X', 'Y', 'Z']) {
    number(box[`min${axis}`], -1e7, 1e7, 'collider minimum'); number(box[`max${axis}`], -1e7, 1e7, 'collider maximum');
    if (box[`min${axis}`] >= box[`max${axis}`]) fail('Collider must have positive dimensions');
  }
  const o = districtOrigin(region, districtId);
  return {minX: box.minX + o.x, maxX: box.maxX + o.x, minY: box.minY, maxY: box.maxY, minZ: box.minZ + o.z, maxZ: box.maxZ + o.z};
}
/** Current assigned cell wins, then nearest assigned cells within a square ring budget. */
export function selectDistrictInterest(region, global, {radius = 1, budget = 9} = {}) {
  number(radius, 0, 63, 'interest radius', true); number(budget, 1, 64, 'interest budget', true);
  const p = point(global), cell = locateCell(region, p);
  if (!cell) return [];
  return region.districts.map(d => {
    const ring = Math.max(Math.abs(d.column - cell.column), Math.abs(d.row - cell.row)), o = cellOrigin(region, d.column, d.row);
    return {district: d, ring, distance: (o.x - p.x) ** 2 + (o.z - p.z) ** 2};
  }).filter(d => d.ring <= radius).sort((a, b) => a.ring - b.ring || a.distance - b.distance || a.district.id.localeCompare(b.district.id))
    .slice(0, budget).map(d => d.district);
}
/** Optimistic admission only. Caller must persist/broadcast atomically after admission. */
export function admitRegionRevision(previous, candidate, {baseRevision} = {}) {
  const old = validateRegion(previous), next = validateRegion(candidate);
  if (baseRevision !== old.revision || next.revision !== old.revision + 1) fail('Region changed; rebase required', 'REVISION_CONFLICT');
  if (next.id !== old.id || next.anchorDistrictId !== old.anchorDistrictId) fail('Region and anchor identities are immutable');
  const beforeAnchor = getDistrict(old, old.anchorDistrictId), afterAnchor = getDistrict(next, next.anchorDistrictId);
  if (beforeAnchor.worldId !== afterAnchor.worldId) fail('Anchor city content reference is immutable');
  for (const d of next.districts) {
    const before = old.districts.find(item => item.id === d.id);
    if (!before) continue;
    if (before.worldId !== d.worldId) fail('Existing district content identity is immutable');
    if (d.revision < before.revision) fail('District revision cannot go backwards', 'REVISION_CONFLICT');
    if ((before.column !== d.column || before.row !== d.row) && d.revision <= before.revision) fail('Moved district needs a new revision');
  }
  return next;
}
/** Location descriptors resolve coordinates only; they do not authorize teleportation. */
export function resolveRegionDestination(region, input, {getExternalWorld = () => null} = {}) {
  if (!object(input)) fail('Destination must be an object');
  if (input.kind === 'district') {
    keys(input, ['kind', 'regionId', 'districtId', 'position', 'yaw'], 'district destination');
    if (input.regionId !== region.id) fail('Destination belongs to another region');
    const local = point(input.position), d = getDistrict(region, id(input.districtId));
    if (local.x < -region.tileSize.x / 2 || local.x >= region.tileSize.x / 2 || local.z < -region.tileSize.z / 2 || local.z >= region.tileSize.z / 2) fail('Destination leaves its district');
    return {kind: 'region', regionId: region.id, districtId: d.id, revision: d.revision, position: toRegionPoint(region, d.id, local), yaw: number(input.yaw ?? 0, -Math.PI * 16, Math.PI * 16, 'yaw')};
  }
  if (input.kind === 'external') {
    keys(input, ['kind', 'worldId', 'portalId'], 'external destination');
    const worldId = id(input.worldId), portalId = id(input.portalId);
    if (region.districts.some(d => d.worldId === worldId) || worldId === region.id) fail('Use a district destination for internal content');
    const world = getExternalWorld(worldId), portal = world?.portals?.find(p => p.id === portalId);
    if (world?.id !== worldId || !portal) fail('External portal is unavailable', 'DESTINATION_UNAVAILABLE');
    return {kind: 'external', worldId, portalId, revision: revision(world.revision), position: point(portal.position), yaw: number(portal.yaw ?? 0, -Math.PI * 16, Math.PI * 16, 'yaw')};
  }
  fail('Unsupported destination kind');
}
/** Admission guard for source content bounds; never silently scales/clips assets. */
export function validateDistrictFootprint(region, districtId, bounds) {
  getDistrict(region, districtId);
  keys(bounds, ['minX', 'maxX', 'minZ', 'maxZ'], 'district footprint');
  for (const axis of ['X', 'Z']) {
    number(bounds[`min${axis}`], -1e7, 1e7, 'footprint minimum'); number(bounds[`max${axis}`], -1e7, 1e7, 'footprint maximum');
    if (bounds[`min${axis}`] >= bounds[`max${axis}`]) fail('Footprint must have positive area');
    const half = region.tileSize[axis.toLowerCase()] / 2;
    if (bounds[`min${axis}`] < -half || bounds[`max${axis}`] > half) fail('District content exceeds cell; enlarge tiles explicitly', 'DISTRICT_OVERFLOW');
  }
  return {...bounds};
}
